import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { getResultsRecords } from "../lib/api";
import Sidebar from "../components/Sidebar";

// ── Constants ─────────────────────────────────────────────────────────────────

const NAICS_SECTORS = {
  '11': 'Agriculture, Forestry & Fishing',
  '21': 'Mining, Quarrying & Oil/Gas',
  '22': 'Utilities',
  '23': 'Construction',
  '31': 'Manufacturing',
  '42': 'Wholesale Trade',
  '44': 'Retail Trade',
  '48': 'Transportation & Warehousing',
  '51': 'Information',
  '52': 'Finance & Insurance',
  '53': 'Real Estate & Rental',
  '54': 'Professional & Technical Services',
  '55': 'Management of Companies',
  '56': 'Administrative & Support Services',
  '61': 'Educational Services',
  '62': 'Health Care & Social Assistance',
  '71': 'Arts, Entertainment & Recreation',
  '72': 'Accommodation & Food Services',
  '81': 'Other Services',
  '92': 'Public Administration',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function confidenceBadge(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return null;
  const pct   = n > 1 ? n : Math.round(n * 100);
  const color = pct >= 90 ? "#10b981" : pct >= 70 ? "#f59e0b" : "#ef4444";
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
      background: `${color}18`, color, marginLeft: 6, letterSpacing: "0.03em" }}>
      {pct}%
    </span>
  );
}

function getFilterTitle(filter, sector) {
  const FILTER_TITLES = {
    all:          "All Records",
    names:        "Company Names Cleaned",
    address:      "Addresses Standardized",
    domain:       "Websites Found",
    naics:        "NAICS Codes Assigned",
    naics_sector: `${NAICS_SECTORS[sector] || sector || ""} Sector`,
    profile:      "Business Profiles",
    person:       "People Identified",
    internal:     "Intercompany Records",
    error:        "Errors",
  };
  return FILTER_TITLES[filter] || "Records";
}

function getRelevantHeader(filter) {
  if (filter === "naics" || filter === "naics_sector") return "NAICS";
  if (filter === "address") return "City / State";
  if (filter === "profile") return "About";
  if (filter === "person")  return "Type";
  if (filter === "internal") return "Type";
  if (filter === "error")   return "Error";
  if (filter === "names")   return "Domain / NAICS";
  return "Domain";
}

function RelevantCell({ filter, row }) {
  if (filter === "domain" || filter === "all") {
    return (
      <td style={styles.td}>
        {row.domain_value
          ? <>{row.domain_value}{confidenceBadge(row.domain_confidence)}</>
          : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
    );
  }
  if (filter === "naics" || filter === "naics_sector") {
    return (
      <td style={styles.td}>
        {row.naics_code
          ? <>
              <span style={{ fontFamily: "monospace", fontSize: 12, color: "#0176CE", marginRight: 6 }}>{row.naics_code}</span>
              <span style={{ color: "#6b7280", fontSize: 12 }}>{truncate(row.naics_title, 30)}</span>
            </>
          : <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
    );
  }
  if (filter === "address") {
    const loc = [row.city_std, row.state_std].filter(Boolean).join(", ");
    return <td style={styles.td}><span style={{ fontSize: 13, color: "#374151" }}>{loc || "—"}</span></td>;
  }
  if (filter === "profile") {
    return (
      <td style={styles.td}>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{truncate(row.what_they_do, 60) || "—"}</span>
      </td>
    );
  }
  if (filter === "person") {
    return (
      <td style={styles.td}>
        <span style={styles.badgeAmber}>Person</span>
      </td>
    );
  }
  if (filter === "internal") {
    return (
      <td style={styles.td}>
        <span style={styles.badgePurple}>Internal</span>
      </td>
    );
  }
  if (filter === "error") {
    return (
      <td style={styles.td}>
        <span style={{ fontSize: 12, color: "#ef4444" }}>{truncate(row.error_message, 50) || "Error"}</span>
      </td>
    );
  }
  if (filter === "names") {
    return (
      <td style={styles.td}>
        {row.domain_value && (
          <div style={{ fontSize: 12, color: "#0176CE", marginBottom: 2 }}>{row.domain_value}</div>
        )}
        {row.naics_code && (
          <div style={{ fontFamily: "monospace", fontSize: 11, color: "#0176CE" }}>{row.naics_code}</div>
        )}
        {!row.domain_value && !row.naics_code && <span style={{ color: "#d1d5db" }}>—</span>}
      </td>
    );
  }
  return <td style={styles.td}>—</td>;
}

function StatusBadge({ ok }) {
  if (ok) return (
    <span style={{ fontSize: 11, fontWeight: 600, color: "#10b981",
      background: "rgba(16,185,129,0.1)", borderRadius: 5, padding: "2px 8px" }}>
      ✓ OK
    </span>
  );
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: "#ef4444",
      background: "rgba(239,68,68,0.08)", borderRadius: 5, padding: "2px 8px" }}>
      ✗ Error
    </span>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TableRow({ row, filter, jobId, sector }) {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  const sectorParam = sector ? `&sector=${sector}` : "";

  return (
    <tr
      style={{ ...styles.tr, background: hover ? "#fafbff" : "#fff", cursor: "pointer" }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => navigate(`/results/${jobId}/records/${row.row_number}?filter=${filter}${sectorParam}`)}
    >
      <td style={{ ...styles.td, color: "#9ca3af", fontSize: 12, width: 40 }}>{row.row_number}</td>
      <td style={{ ...styles.td, color: "#6b7280", fontSize: 12 }}>{row.original_num || "—"}</td>
      <td style={styles.td}>
        <span style={{ color: "#9ca3af", fontSize: 12 }}>{row.original_name || "—"}</span>
      </td>
      <td style={styles.td}>
        <span style={{ color: "#111827", fontWeight: 500, fontSize: 13 }}>
          {row.company_name_recommended || row.name1_std || "—"}
        </span>
      </td>
      <RelevantCell filter={filter} row={row} />
      <td style={styles.td}><StatusBadge ok={row.ok} /></td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const LIMIT = 50;

export default function ResultsRecords() {
  const { jobId }          = useParams();
  const navigate           = useNavigate();
  const [searchParams]     = useSearchParams();

  const filter = searchParams.get("filter") || "all";
  const sector = searchParams.get("sector") || "";

  const [results,     setResults]     = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState("");
  const [fileName,    setFileName]    = useState("");

  const fetchPage = useCallback(async (p) => {
    try {
      const res = await getResultsRecords(jobId, { filter, sector, page: p, limit: LIMIT });
      setTotal(res.total || 0);
      if (p === 1) {
        setResults(res.results || []);
      } else {
        setResults(prev => [...prev, ...(res.results || [])]);
      }
      setError("");
    } catch (e) {
      setError(e.message || "Could not load records.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [jobId, filter, sector]);

  useEffect(() => {
    setLoading(true);
    setResults([]);
    setPage(1);
    fetchPage(1);
  }, [fetchPage]);

  function loadMore() {
    const next = page + 1;
    setPage(next);
    setLoadingMore(true);
    fetchPage(next);
  }

  const hasMore     = results.length < total;
  const pageTitle   = getFilterTitle(filter, sector);
  const relevantHdr = getRelevantHeader(filter);
  const sectorParam = sector ? `&sector=${sector}` : "";

  return (
    <div style={styles.page}>
      <div style={styles.topStripe} />
      <div style={styles.body}>
      <Sidebar active="dashboard" />

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <main style={styles.main}>
        {/* Header */}
        <div style={styles.headerRow}>
          <div>
            <Link to={`/results/${jobId}`} style={styles.backLink}>← Back to results</Link>
            <h1 style={styles.pageTitle}>{pageTitle}</h1>
            <p style={styles.pageSub}>
              {total.toLocaleString()} records · Job #{jobId}
            </p>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={styles.centerBox}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Loading records…</p>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={styles.errorCard}>
            <p style={{ color: "#f87171", margin: 0 }}>{error}</p>
            <button onClick={() => { setLoading(true); fetchPage(1); }} style={styles.retryBtn}>Retry</button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div style={styles.tableCard}>
            {results.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⧗</div>
                <p style={{ color: "#6b7280", margin: 0, fontSize: 15 }}>No records found for this filter.</p>
              </div>
            ) : (
              <>
                <div style={styles.tableHeaderBar}>
                  <span style={styles.tableCountLabel}>
                    Showing {results.length.toLocaleString()} of {total.toLocaleString()} records
                  </span>
                </div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Vendor #</th>
                      <th style={styles.th}>Original Name</th>
                      <th style={styles.th}>Cleaned Name</th>
                      <th style={styles.th}>{relevantHdr}</th>
                      <th style={styles.th}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(row => (
                      <TableRow
                        key={row.row_number}
                        row={row}
                        filter={filter}
                        jobId={jobId}
                        sector={sector}
                      />
                    ))}
                  </tbody>
                </table>
                {hasMore && (
                  <div style={{ padding: "16px 24px", borderTop: "1px solid #e9ecf3", textAlign: "center" }}>
                    <button onClick={loadMore} disabled={loadingMore} style={styles.loadMoreBtn}>
                      {loadingMore ? "Loading…" : `Load more (${(total - results.length).toLocaleString()} remaining)`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </main>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page:          { display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" },
  topStripe:     { height: 4, background: "linear-gradient(90deg, #0176CE, #F37428)", flexShrink: 0 },
  body:          { display: "flex", flex: 1 },
  main:          { flex: 1, padding: "40px 48px", overflowY: "auto" },
  headerRow:     { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 28 },
  backLink:      { color: "#6b7280", fontSize: 13, textDecoration: "none", display: "block", marginBottom: 8 },
  pageTitle:     { color: "#111827", fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: "-0.5px" },
  pageSub:       { color: "#6b7280", fontSize: 14, margin: "4px 0 0" },
  centerBox:     { textAlign: "center", padding: "80px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  mutedText:     { color: "#6b7280", marginTop: 16, fontSize: 14 },
  spinner:       { width: 32, height: 32, border: "3px solid #e2e8f0", borderTop: "3px solid #0176CE", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  errorCard:     { background: "#fff", border: "1px solid #fecaca", borderRadius: 12, padding: "32px 24px", textAlign: "center" },
  retryBtn:      { marginTop: 12, background: "transparent", border: "1px solid #0176CE", borderRadius: 6, color: "#0176CE", cursor: "pointer", fontSize: 13, padding: "7px 16px" },
  tableCard:     { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" },
  tableHeaderBar:{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #e2e8f0" },
  tableCountLabel:{ color: "#6b7280", fontSize: 13 },
  table:         { width: "100%", borderCollapse: "collapse" },
  th:            { color: "#6b7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", padding: "10px 16px", textAlign: "left", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" },
  tr:            { borderBottom: "1px solid #f1f5f9", transition: "background 0.1s" },
  td:            { color: "#374151", fontSize: 13, padding: "13px 16px", verticalAlign: "middle" },
  loadMoreBtn:   { background: "transparent", border: "1px solid #e2e8f0", borderRadius: 7, color: "#374151", cursor: "pointer", fontSize: 13, padding: "9px 24px" },
  emptyState:    { textAlign: "center", padding: "60px 24px", display: "flex", flexDirection: "column", alignItems: "center" },
  badgeAmber:    { fontSize: 11, fontWeight: 600, color: "#f59e0b", background: "rgba(245,158,11,0.1)", borderRadius: 5, padding: "2px 8px" },
  badgePurple:   { fontSize: 11, fontWeight: 600, color: "#8b5cf6", background: "rgba(139,92,246,0.1)", borderRadius: 5, padding: "2px 8px" },
};
